import { readScopedStorage, writeScopedStorage } from './userPreferenceScope';
import { playThemeTransition, THEME_TRANSITION_MS, type ThemeTransitionOrigin } from './themeTransition';

export const DARK_MODE_STORAGE_KEY = 'ef_app_dark_mode_v1';
export const DARK_MODE_CHANGED_EVENT = 'ef-dark-mode-changed';

const TRANSITION_MS = THEME_TRANSITION_MS;

export function readDarkMode(): boolean {
  if (typeof localStorage === 'undefined') return false;
  const raw = readScopedStorage(DARK_MODE_STORAGE_KEY);
  return raw === '1' || raw === 'true';
}

export function writeDarkMode(enabled: boolean): void {
  if (typeof localStorage === 'undefined') return;
  writeScopedStorage(DARK_MODE_STORAGE_KEY, enabled ? '1' : '0');
  try {
    window.dispatchEvent(new Event(DARK_MODE_CHANGED_EVENT));
  } catch {
    /* no window */
  }
}

function setSurfaceTokens(enabled: boolean): void {
  const root = document.documentElement;
  if (enabled) {
    root.style.setProperty('--tg-surface', '#09090b');
    root.style.setProperty('--tg-ink', '#fafafa');
    root.style.setProperty('--tg-muted', '#a1a1aa');
    root.style.colorScheme = 'dark';
  } else {
    root.style.removeProperty('--tg-surface');
    root.style.removeProperty('--tg-ink');
    root.style.removeProperty('--tg-muted');
    root.style.colorScheme = 'light';
  }
}

/** Apply dark mode class + surface tokens. Optionally animate the transition. */
export function applyDarkMode(
  enabled: boolean,
  animate = false,
  origin?: ThemeTransitionOrigin
): void {
  if (typeof document === 'undefined') return;

  const apply = () => {
    const root = document.documentElement;
    if (animate) {
      root.classList.add('dark-mode-transition');
    }

    root.classList.toggle('dark', enabled);
    setSurfaceTokens(enabled);

    if (animate) {
      window.setTimeout(() => {
        root.classList.remove('dark-mode-transition');
      }, TRANSITION_MS);
    }
  };

  if (animate && origin) {
    playThemeTransition(enabled, origin, apply);
    return;
  }

  apply();
}

export function toggleDarkMode(animate = true, origin?: ThemeTransitionOrigin): boolean {
  const next = !readDarkMode();
  writeDarkMode(next);
  applyDarkMode(next, animate, origin);
  return next;
}
